import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface Disclosure<T extends HTMLElement> {
  readonly isOpen: boolean;
  readonly containerRef: RefObject<T | null>;
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
}

export function useDisclosure<T extends HTMLElement>(): Disclosure<T> {
  const containerRef = useRef<T | null>(null);
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: PointerEvent): void => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [isOpen]);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  return { isOpen, containerRef, open, close, toggle };
}
